class GiftingOptions extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setMaxHeight()
  }

  setupListeners () {
    this.updateGiftOption = this.updateGiftOption.bind(this)
    this.updateInnerOverlay = this.updateInnerOverlay.bind(this)
    this.clearSelections = this.clearSelections.bind(this)
    this.clearCartNote = this.clearCartNote.bind(this)
    this.checkMessageValue = this.checkMessageValue.bind(this)
    this.populateExistingGiftMessage = this.populateExistingGiftMessage.bind(this)
    this.openOptions = this.openOptions.bind(this)
    this.submitGiftMessage = this.submitGiftMessage.bind(this)
    this.markAsAdded = this.markAsAdded.bind(this)
    this.clearGiftOption = this.clearGiftOption.bind(this)
    this.handleCartItemRemoved = this.handleCartItemRemoved.bind(this)

    // Use event delegation for edit triggers since they may be dynamically added
    document.addEventListener('click', (event) => {
      if (event.target.matches('[data-gifting-options-external-el="editTrigger"]') ||
          event.target.closest('[data-gifting-options-external-el="editTrigger"]')) {
        this.openOptions(event)
      }
    })

    for (const optionEl of this.els.option.elements) {
      this.listeners.add(optionEl, 'click', this.updateGiftOption)
    }

    // Use direct query for inner overlay
    const innerOverlay = document.querySelector('[data-gifting-options-external-el="innerOverlay"]')
    if (innerOverlay) {
      this.listeners.add(innerOverlay, 'click', this.updateInnerOverlay)
    }

    // Use event delegation for accordion triggers
    document.addEventListener('click', (event) => {
      if (event.target.matches('[data-gifting-options-external-el="accordionTrigger"]') ||
          event.target.closest('[data-gifting-options-external-el="accordionTrigger"]')) {
        this.updateInnerOverlay(event)
      }
    })

    this.listeners.add(this.els.clear.element, 'click', this.clearGiftOption)
    this.listeners.add(this.els.message.element, 'input', this.checkMessageValue)
    this.listeners.add(this.els.submit.element, 'click', this.submitGiftMessage)
    this.listeners.add(window, 'load', this.populateExistingGiftMessage)

    // Listen for cart item removals to clear note when needed
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.handleCartItemRemoved,
    )
  }

  reInit () {
    this.listeners.removeAll()
    this.setupListeners()
  }

  async clearGiftOption () {
    await this.clearCartNote()
    this.clearSelections()
    this.clearTextarea()
    this.markAsAdded(false)
    this.hideNote()

    // If on main cart, update the display without reloading
    if (this.isMainCart) {
      this.updateGiftMessageDisplay('')
    }
  }

  async submitGiftMessage (event) {
    event.preventDefault()

    const form = event.target.closest('form')

    // If it's the main cart page, handle differently
    if (this.isMainCart) {
      // Get the message content
      const message = this.els.message.element.value

      // Update cart note using the fetch API
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: message }),
      })
      .then(response => response.json())
      .then(data => {
        // Mark as added and update UI without reloading page
        this.markAsAdded(true)
        this.updateInnerOverlay(false)

        // Add or update the gift message display
        this.updateGiftMessageDisplay(message)
      })
      .catch((error) => {
        console.error('Error:', error)
      })
    } else {
      // Original behavior for non-cart pages
      await window.BAO.Cart.addItemFromForm(form)
      this.markAsAdded(true)
      this.updateInnerOverlay(false)
    }

    this.reInit()
  }

  async populateExistingGiftMessage () {
    const cart = await window.BAO.Utils.fetchJSON('/cart.json')

    if (!cart.note) {
      return
    }

    this.els.message.element.value = cart.note
    this.markAsAdded(true)

    // If this is the main cart, update the display
    if (this.isMainCart) {
      this.updateGiftMessageDisplay(cart.note)
    }
  }

  openOptions () {
    // Find the accordion trigger - using querySelector directly to get the most recent element
    const accordionTrigger = document.querySelector('[data-gifting-options-external-el="accordionTrigger"]')

    if (accordionTrigger) {
      // Check if accordion is closed (aria-expanded="false")
      const accordionElement = accordionTrigger.closest('[data-accordion-group-el="item"]')
      if (accordionElement && accordionElement.getAttribute('aria-expanded') === 'false') {
        // Trigger a click to open it
        accordionTrigger.click()
      }
    }

    // Focus on the message textarea after a short delay to ensure the accordion is open
    setTimeout(() => {
      const messageTextarea = this.querySelector('[data-gifting-options-el="message"]')
      if (messageTextarea) {
        messageTextarea.focus()
      }
    }, 300)

    this.setScrollOnDrawer(true)
  }

  setScrollOnDrawer (hide = false) {
    if (!this.externalEls?.drawer?.exists) return

    this.externalEls.drawer.element.style.overflowY = hide ? 'hidden' : 'auto'
  }

  updateGiftOption (event) {
    const value = event.target.value

    this.els.hiddenGiftOption.element.value = value
    this.els.hiddenGiftOptionQuantity.element.value = 1

    this.updateClearButton()
  }

  checkMessageValue (event) {
    if (this.els.message.element.value.length > 0) {
      this.els.clear.element.setAttribute('aria-hidden', 'false')
    } else {
      this.els.clear.element.setAttribute('aria-hidden', 'true')
    }

    this.updateHiddenGiftMessage(event)
  }

  updateHiddenGiftMessage (event) {
    if (!this.els.hiddenCartMessage?.element?.exists) return

    this.els.hiddenCartMessage.element.value = event.target.value
  }

  updateClearButton () {
    const anyOptionSelected = this.els.option.elements.some(optionEl => optionEl.checked)

    this.els.clear.element.setAttribute('aria-hidden', anyOptionSelected ? 'false' : 'true')
  }

  async clearCartNote () {
    try {
      const cart = await window.BAO.Utils.fetchJSON('/cart.json')
      if (!cart.note) return

      cart.note = ''

      await window.BAO.Utils.fetchJSON('/cart/update.json', {
        method: 'POST',
        body: JSON.stringify(cart),
      })

      this.hideNote()
    } catch (error) {
      console.error('Error clearing cart note:', error)
    }
  }

  hideNote () {
    // Use direct querySelector instead of relying on externalEls
    const noteElement = document.querySelector('[data-gifting-options-external-el="note"]')
    if (noteElement) {
      noteElement.style.display = 'none'
    }
    this.clearTextarea()
  }

  clearSelections () {
    // Check if options exist before iterating
    if (this.els.option?.elements) {
      for (const optionEl of this.els.option.elements) {
        optionEl.checked = false
      }
    }

    // Add null checks for all element accesses
    if (this.els.hiddenGiftOption?.element) {
      this.els.hiddenGiftOption.element.value = ''
    }

    if (this.els.clear?.element) {
      this.els.clear.element.setAttribute('aria-hidden', 'true')
    }

    if (this.els.hiddenGiftOptionQuantity?.element) {
      this.els.hiddenGiftOptionQuantity.element.value = 0
    }
  }

  clearTextarea () {
    // Use direct querySelector instead of relying on els
    const messageElement = document.querySelector('[data-gifting-options-el="message"]')
    if (messageElement) {
      messageElement.value = ''
    }
  }

  updateInnerOverlay (event) {
    const innerOverlay = document.querySelector('[data-gifting-options-external-el="innerOverlay"]')
    if (!innerOverlay) return

    let isHidden = true

    const target = event && event.target
    const accordion = target && typeof target.closest === 'function'
      ? target.closest('[data-gifting-options-external-el="accordion"]')
      : null
    if (accordion) {
      isHidden = accordion.getAttribute('aria-expanded') === 'true'

      this.setMaxHeight()
    } else {
      isHidden = innerOverlay.getAttribute('aria-hidden') === 'true'
    }

    innerOverlay.setAttribute('aria-hidden', isHidden ? 'false' : 'true')

    this.setScrollOnDrawer(isHidden)

    if (event === false || event.target === innerOverlay) {
      this.updateActiveAccordions()
    }
  }

  setMaxHeight () {
    const header = this.externalEls?.header?.element
    const recommendations = this.externalEls?.recommendations?.element
    const footer = this.externalEls?.footer?.element
    const giftingOptionsContainer = this.externalEls?.container?.element
    if (!header || !recommendations || !footer || !giftingOptionsContainer) return

    const maxHeight = window.innerHeight
      - header.getBoundingClientRect().height
      - footer.getBoundingClientRect().height
      - recommendations.getBoundingClientRect().height
      - 40 // Additional padding

    giftingOptionsContainer.style.maxHeight = `${maxHeight}px`
  }

  updateActiveAccordions () {
    const activeAccordions = this.externalEls.accordionTrigger.elements.filter(accordion => {
      const parent = accordion.closest('[data-accordion-group-el="item"]')
      return parent.getAttribute('aria-expanded') === 'true'
    })

    for (const accordion of activeAccordions) {
      const parent = accordion.closest('[data-accordion-group-el="item"]')
      parent.setAttribute('aria-expanded', 'false')
    }
  }

  markAsAdded (add = true) {
    const accordionTrigger = document.querySelector('[data-gifting-options-external-el="accordionTrigger"]')
    if (accordionTrigger) {
      accordionTrigger.setAttribute('data-has-been-added', add ? 'true' : 'false')
    }
  }

  async handleCartItemRemoved () {
    try {
      const cart = await window.BAO.Utils.fetchJSON('/cart.json')

      if (cart.items.length === 0) {
        await this.clearCartNote()
      }
    } catch (error) {
      console.error('Error handling cart change:', error)
    }
  }

  updateGiftMessageDisplay(message) {
    if (!this.isMainCart) return

    // Find parent container
    const parentContainer = this.closest('.gft-GiftItems_Section')
    if (!parentContainer) return

    // Check if the gift note display already exists
    let giftNoteElement = parentContainer.querySelector('.gft-GiftItems_CartNote')

    if (message && message.trim() !== '') {
      if (!giftNoteElement) {
        giftNoteElement = document.createElement('div')
        giftNoteElement.className = 'gft-GiftItems_CartNote'
        giftNoteElement.innerHTML = `
          <div class="gft-GiftItems_CartNoteContainer">
            <div class="gft-GiftItems_CartNoteTitle">
              <p>Gift Message</p>
            </div>
            <div class="gft-GiftItems_CartNoteText">${message.replace(/\n/g, '<br>')}</div>
          </div>
          <button class="gft-GiftItems_CartNoteEdit" type="button" data-gifting-options-external-el="editTrigger">Edit</button>
        `
        parentContainer.appendChild(giftNoteElement)

        // Get the edit button we just added
        const newEditButton = giftNoteElement.querySelector('[data-gifting-options-external-el="editTrigger"]')

        // Add direct event listener to this specific button to ensure it works immediately
        if (newEditButton) {
          newEditButton.addEventListener('click', () => {
            // Find the accordion trigger directly with querySelector
            const accordionTrigger = document.querySelector('[data-gifting-options-external-el="accordionTrigger"]')
            if (accordionTrigger) {
              // Check if accordion is closed (aria-expanded="false")
              const accordionElement = accordionTrigger.closest('[data-accordion-group-el="item"]')
              if (accordionElement && accordionElement.getAttribute('aria-expanded') === 'false') {
                // Trigger a click to open it
                accordionTrigger.click()
              }
            }

            // Focus on the message textarea after a short delay to ensure the accordion is open
            setTimeout(() => {
              const messageTextarea = document.querySelector('[data-gifting-options-el="message"]')
              if (messageTextarea) {
                messageTextarea.focus()
              }
            }, 300)
          })
        }
      } else {
        // Update existing gift note
        const noteTextElement = giftNoteElement.querySelector('.gft-GiftItems_CartNoteText')
        if (noteTextElement) {
          noteTextElement.innerHTML = message.replace(/\n/g, '<br>')
        }
      }
    } else if (giftNoteElement) {
      // Remove the gift note display if message is empty
      giftNoteElement.remove()
    }
  }

  get isMainCart () {
    return this.hasAttribute('data-main-cart')
  }
}

customElements.define('gifting-options', GiftingOptions)
